import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { Form, Input, Modal, Select, Skeleton } from 'antd'
import { ChannelSwitch } from '@pushly/aqe/lib/components/channel-switch/channel-switch'
import { FileUploader } from '@pushly/aqe/lib/components'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import moment from 'moment-timezone'
import './new-domain-modal.scss'
import { DomainDto } from '@halo/dtos/domain.dto'
import { AccountDto } from '@halo/dtos/account.dto'
import { AccountService } from '@halo/services/account.service'
import { DomainService } from '@halo/services/domain.service'
import { ApiEnumService } from '@halo/services/api-enum.service'
import { DEFAULT_DOMAIN_INDUSTRY_ID, DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { prepareWhitelistDomains } from '@halo/_utils/domain'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { Industry } from '@pushly/models/lib/structs/industry'

// ─── Types ────────────────────────────────────────────────────────────────────

export type NewDomainModalMode = 'standard' | 'demo' | 'demo-template'

export interface NewDomainModalProps {
    visible: boolean
    mode: NewDomainModalMode
    /** When provided, scopes the domain to this org (hides org selector) */
    accountId?: number
    /** When provided, pre-selects this template in demo mode */
    initialTemplateId?: number
    /** When provided, pre-fills form fields from this domain */
    initialValues?: Partial<DomainDto>
    onOk: (domainDto: Partial<DomainDto>) => Promise<void> | void
    onCancel: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MODAL_TITLES: Record<NewDomainModalMode, string> = {
    standard: 'New Domain',
    demo: 'Create Demo Domain',
    'demo-template': 'New Demo Template Domain',
}

const OK_TEXT: Record<NewDomainModalMode, string> = {
    standard: 'Create Domain',
    demo: 'Create Demo Domain',
    'demo-template': 'Create Demo Template Domain',
}

const PROTOCOL_RE = /^https?:\/\//i

const CHANNEL_FLAGS = {
    web: 'FEAT_CHANNEL_WEB',
    ios: 'FEAT_CHANNEL_IOS',
    android: 'FEAT_CHANNEL_ANDROID',
} as const

// ─── Component ────────────────────────────────────────────────────────────────

export function NewDomainModal({ visible, mode, accountId, initialTemplateId, initialValues, onOk, onCancel }: NewDomainModalProps) {
    const [form] = Form.useForm()

    const accountService = Container.get(AccountService)
    const domainService = Container.get(DomainService)
    const apiEnumService = Container.get(ApiEnumService)

    // Reference data
    const [accounts, setAccounts] = useState<AccountDto[]>([])
    const [currencies, setCurrencies] = useState<any[]>([])
    const [industries, setIndustries] = useState<Industry[]>([])
    const [templates, setTemplates] = useState<DomainDto[]>([])

    // UI state
    const [isRefLoading, setIsRefLoading] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
    const [iconUrl, setIconUrl] = useState<string | undefined>(undefined)
    const [activeChannels, setActiveChannels] = useState<DeliveryChannel[]>([
        DeliveryChannel.WEB,
        DeliveryChannel.NATIVE_IOS,
        DeliveryChannel.NATIVE_ANDROID,
    ])

    const displayNameInputRef = useRef<any>(null)

    const isDemo = mode === 'demo'
    const isDemoTemplate = mode === 'demo-template'
    const isStandard = mode === 'standard'

    // ── Data fetching ──────────────────────────────────────────────────────────

    useEffect(() => {
        if (!visible) return

        setIsRefLoading(true)

        const fetches: Promise<void>[] = [fetchCurrencies(), fetchIndustries()]

        if (!accountId) {
            fetches.push(fetchAccounts())
        }

        if (isDemo) {
            fetches.push(fetchTemplates())
        }

        Promise.all(fetches).then(() => setIsRefLoading(false))
    }, [visible, mode])

    // Reset form and local state when modal opens
    useEffect(() => {
        if (visible) {
            // Apply initialValues if provided (clone scenario)
            if (initialValues) {
                form.setFieldsValue({
                    displayName: initialValues.display_name,
                    name: initialValues.name,
                    timezone: initialValues.timezone,
                    currencyCode: initialValues.currency_code,
                    industry: initialValues.industry_id,
                })
                if (initialValues.default_icon_url) setIconUrl(initialValues.default_icon_url)
                // Pre-set channels from flags
                if (initialValues.flags?.length) {
                    const channels: DeliveryChannel[] = []
                    if (initialValues.flags.includes(CHANNEL_FLAGS.web)) channels.push(DeliveryChannel.WEB)
                    if (initialValues.flags.includes(CHANNEL_FLAGS.ios)) channels.push(DeliveryChannel.NATIVE_IOS)
                    if (initialValues.flags.includes(CHANNEL_FLAGS.android)) channels.push(DeliveryChannel.NATIVE_ANDROID)
                    if (channels.length) setActiveChannels(channels)
                }
            } else {
                setIconUrl(undefined)
                setActiveChannels([DeliveryChannel.WEB, DeliveryChannel.NATIVE_IOS, DeliveryChannel.NATIVE_ANDROID])
            }
            setTimeout(() => displayNameInputRef.current?.focus(), 50)
        }
    }, [visible])

    async function fetchAccounts() {
        const { ok, data } = await accountService.fetchAll({ query: { pagination: 0 }, showLoadingScreen: false })
        if (ok) setAccounts(data)
    }

    async function fetchCurrencies() {
        const { ok, data } = await apiEnumService.fetchAllCurrencies({ query: { pagination: 0 }, showLoadingScreen: false })
        if (ok) setCurrencies(data)
    }

    async function fetchIndustries() {
        const { ok, data } = await apiEnumService.fetchAllIndustries({ query: { pagination: 0 }, showLoadingScreen: false })
        if (ok) setIndustries(data)
    }

    // When templates load, pre-select initialTemplateId if provided
    useEffect(() => {
        if (templates.length === 0) return
        if (!initialTemplateId) {
            setSelectedTemplateId(null)
            return
        }
        const tmpl = templates.find((t) => t.id === initialTemplateId)
        if (!tmpl) return
        setSelectedTemplateId(tmpl.id)
        form.setFieldsValue({
            currencyCode: tmpl.currency_code ?? 'USD',
            industry: tmpl.industry_id ?? DEFAULT_DOMAIN_INDUSTRY_ID,
        })
    }, [templates])

    async function fetchTemplates() {
        const { ok, data } = await domainService.fetchAll({ query: { is_demo_template: true, pagination: 0 }, showLoadingScreen: false })
        if (ok) setTemplates(data)
    }

    // ── Template selection ─────────────────────────────────────────────────────

    function handleTemplateChange(templateId: number) {
        const tmpl = templates.find((t) => t.id === templateId)
        if (!tmpl) return

        setSelectedTemplateId(tmpl.id)
        form.setFieldsValue({
            currencyCode: tmpl.currency_code ?? 'USD',
            industry: tmpl.industry_id ?? DEFAULT_DOMAIN_INDUSTRY_ID,
        })
    }

    // ── Submit ─────────────────────────────────────────────────────────────────

    async function handleOk() {
        if (isDemo && !selectedTemplateId) {
            simpleNotification('error', 'Please select a demo template.')
            return
        }

        setIsLoading(true)

        try {
            await form.validateFields()
            const values = form.getFieldsValue()

            if (values.name && PROTOCOL_RE.test(values.name)) {
                values.name = values.name.replace(PROTOCOL_RE, '')
            }

            const dto: Partial<DomainDto> = {
                account_id: accountId ?? values.organization,
                display_name: values.displayName,
                name: values.name,
                timezone: values.timezone,
                currency_code: values.currencyCode ?? 'USD',
                industry_id: values.industry ?? DEFAULT_DOMAIN_INDUSTRY_ID,
                default_icon_url: iconUrl || undefined,
            }

            if (isStandard) {
                dto.whitelist_domains = prepareWhitelistDomains(values.whitelistDomains ?? [])
            }

            if (isDemo) {
                dto.template_domain_id = selectedTemplateId
                // Build channel flags from active channel selection
                const channelFlags: string[] = ['FEAT_APP_MESSAGES']
                if (activeChannels.includes(DeliveryChannel.WEB)) channelFlags.push(CHANNEL_FLAGS.web)
                if (activeChannels.includes(DeliveryChannel.NATIVE_IOS)) channelFlags.push(CHANNEL_FLAGS.ios)
                if (activeChannels.includes(DeliveryChannel.NATIVE_ANDROID)) channelFlags.push(CHANNEL_FLAGS.android)
                dto.flags = channelFlags
            }

            if (isDemoTemplate) {
                dto.is_demo_template = true
            }

            try {
                await onOk(dto)
            } finally {
                setIsLoading(false)
            }
        } catch (err) {
            setIsLoading(false)
            simpleFormErrorNotification(err)
        }
    }

    function handleCancel() {
        form.resetFields()
        onCancel()
    }

    // ── Render ─────────────────────────────────────────────────────────────────

    const timezones = moment.tz.names()
    const suggestedTimezone = moment.tz.guess()

    const parentTz = accountId
        ? accounts.find((a) => a.id.toString() === accountId.toString())?.timezone
        : undefined

    // Group templates by industry for OptGroup rendering
    const templatesByIndustry = industries.reduce<Array<{ industry: Industry; templates: DomainDto[] }>>((acc, ind) => {
        const group = templates.filter((t) => t.industry_id === ind.id)
        if (group.length > 0) acc.push({ industry: ind, templates: group })
        return acc
    }, [])

    // Templates with no matching industry go into a fallback group
    const ungroupedTemplates = templates.filter((t) => !industries.some((ind) => ind.id === t.industry_id))

    return (
        <Modal
            className="new-domain-modal"
            title={MODAL_TITLES[mode]}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={isLoading}
            okText={OK_TEXT[mode]}
            width={640}
            destroyOnClose={true}
            styles={{ body: { maxHeight: 'calc(100dvh - 220px)', overflowY: 'auto' } }}
        >
            <Skeleton active={true} loading={isRefLoading} paragraph={{ rows: isDemo ? 8 : 6 }}>
                <Form form={form} {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT} labelAlign="left" requiredMark={false}>
                    {/* Template picker — demo mode only */}
                    {isDemo && (
                        <Form.Item
                            label="Template"
                            required={true}
                        >
                            <Select
                                size="small"
                                showSearch={true}
                                placeholder="Select a template..."
                                value={selectedTemplateId ?? undefined}
                                onChange={handleTemplateChange}
                                optionFilterProp="label"
                                notFoundContent={isRefLoading ? 'Loading...' : 'No templates available'}
                            >
                                {templatesByIndustry.map(({ industry, templates: group }) => (
                                    <Select.OptGroup key={industry.id} label={industry.name}>
                                        {group.map((tmpl) => (
                                            <Select.Option key={tmpl.id} value={tmpl.id} label={tmpl.display_name}>
                                                {tmpl.display_name}
                                            </Select.Option>
                                        ))}
                                    </Select.OptGroup>
                                ))}
                                {ungroupedTemplates.map((tmpl) => (
                                    <Select.Option key={tmpl.id} value={tmpl.id} label={tmpl.display_name}>
                                        {tmpl.display_name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {/* Org selector — only when not scoped to a specific org */}
                    {!accountId && (
                        <Form.Item
                            name="organization"
                            label="Organization"
                            rules={[{ required: true, message: 'Organization is required.' }]}
                        >
                            <Select size="small">
                                {accounts.map((account) => (
                                    <Select.Option key={account.id} value={account.id.toString()}>
                                        {account.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item
                        name="displayName"
                        label="Display Name"
                        rules={[{ required: true, message: 'Display name is required.' }]}
                    >
                        <Input ref={displayNameInputRef} size="small" autoComplete="off" />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Domain URL"
                        rules={[{ required: true, message: 'Domain URL is required.' }]}
                    >
                        <Input size="small" autoComplete="off" />
                    </Form.Item>

                    <Form.Item
                        name="timezone"
                        label="Time Zone"
                        initialValue={parentTz ?? suggestedTimezone}
                        rules={[{ required: true, message: 'Time Zone is required.' }]}
                    >
                        <Select size="small" showSearch={true} placeholder="Select a timezone">
                            {timezones.map((tz: string) => (
                                <Select.Option key={tz} value={tz}>{tz}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="currencyCode"
                        label="Currency"
                        initialValue="USD"
                        rules={[{ required: true, message: 'Currency is required.' }]}
                    >
                        <Select size="small" showSearch={true} placeholder="Select a currency" optionFilterProp="title">
                            {currencies.map((curr: any) => (
                                <Select.Option key={curr.code} value={curr.code} title={`${curr.code} ${curr.name}`}>
                                    {curr.code} — {curr.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="industry"
                        label="Industry"
                        initialValue={DEFAULT_DOMAIN_INDUSTRY_ID}
                        rules={[{ required: true, message: 'Industry is required.' }]}
                    >
                        <Select size="small" showSearch={true} placeholder="Select an industry" optionFilterProp="title">
                            {industries.map((industry: Industry) => (
                                <Select.Option key={industry.id} value={industry.id} title={industry.name}>
                                    {industry.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Icon">
                        <FileUploader
                            type="image"
                            ownerType="org"
                            ownerId={accountId ?? form.getFieldValue('organization')}
                            value={iconUrl ? { url: iconUrl } : undefined}
                            onSuccess={(upload: any) => setIconUrl(upload?.url)}
                            onRemove={() => setIconUrl(undefined)}
                        />
                    </Form.Item>

                    {/* Channel toggles — demo mode only (template domains always get all channels) */}
                    {isDemo && (
                        <Form.Item label="Channels">
                            <div style={{ display: 'flex', gap: 16 }}>
                                {[DeliveryChannel.WEB, DeliveryChannel.NATIVE_IOS, DeliveryChannel.NATIVE_ANDROID].map((ch) => (
                                    <div key={ch} className="channel-switch-wrapper">
                                        <ChannelSwitch
                                            channel={ch}
                                            value={activeChannels}
                                            onChange={(updated: DeliveryChannel[]) => setActiveChannels([...updated])}
                                            iconOptions={{ showIcon: true }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </Form.Item>
                    )}

                    {/* SDK Allow List — standard domains only */}
                    {isStandard && (
                        <Form.Item name="whitelistDomains" label="SDK Allow List" initialValue={[]}>
                            <Select
                                size="small"
                                mode="tags"
                                className="domains-allowed stacked-tags"
                                placeholder="https://www.pushly.com, https://subdomain.pushly.com"
                                tokenSeparators={[',', ';', ' ', '\n']}
                                notFoundContent=""
                            />
                        </Form.Item>
                    )}
                </Form>
            </Skeleton>
        </Modal>
    )
}
